/**
 * F-004 US3 — historical-audit-view route. Read-only render of a
 * specific persisted audit row by id. Re-uses <AuditReport> from US2
 * with rerun disabled. Banner fires when the row's stamps differ from
 * the response's currentRubricVersion (Codex F3).
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Spinner } from '@/components/atoms/Spinner';
import { useAuditById } from '@/lib/api/auditApi';
import { AuditReport } from '@/features/audit/AuditReport';

export const Route = createFileRoute('/projects/$projectId/audit/$auditId')({
  component: HistoricalAuditPage,
});

function HistoricalAuditPage() {
  const { projectId, auditId } = useParams({ strict: false }) as {
    projectId?: string;
    auditId?: string;
  };
  const q = useAuditById(projectId, auditId);

  if (!projectId || !auditId) return null;

  if (q.isLoading) {
    return <div className="flex h-64 items-center justify-center"><Spinner aria-label="Loading audit" /></div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="font-display text-title-sm">Audit not found.</h1>
        <p className="mt-2 text-body-sm text-muted-foreground">
          This audit may have been pruned beyond the retention window, or it may belong to another project.
        </p>
        <Link
          to="/projects/$projectId/workspace/$tab"
          params={{ projectId, tab: 'pro-forma' }}
          className="mt-4 inline-block text-caption text-primary hover:underline"
        >
          ← Back to the Audit tab
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <Link
        to="/projects/$projectId/workspace/$tab"
        params={{ projectId, tab: 'pro-forma' }}
        className="text-caption text-primary hover:underline"
      >
        ← Back to the Audit tab
      </Link>
      <AuditReport
        report={q.data}
        rerunPending={false}
        onRerun={() => { /* Re-run disabled on historical view */ }}
      />
    </div>
  );
}
